import type { SymDef } from './types';

/*
 * Fluid power (ISO 1219-1: pneumatics, hydraulics) and process (ISO 10628 /
 * ISA 5.1 P&ID) symbols. Pipes are drawn with the ordinary wire tool; these
 * nets get no wire numbers. Device letters follow IEC 81346-2 (MM cylinder,
 * QM valve, GP pump, HQ filter, CM tank, BP/BT… sensors). A solenoid valve
 * names its coils in props.sol / props.sol2; the coils are the "MB" valve
 * solenoids of the electrical schematic and both sides cross-reference.
 */

const P = (d: string, c?: string) => `<path d="${d}"${c ? ` class="${c}"` : ''}/>`;
const R = (x: number, y: number, w: number, h: number) => `<rect x="${x}" y="${y}" width="${w}" height="${h}"/>`;
const C = (x: number, y: number, r: number) => `<circle cx="${x}" cy="${y}" r="${r}"/>`;

/** Flow arrow from (x1,y1) to (x2,y2) with a filled head. */
function arrow(x1: number, y1: number, x2: number, y2: number, hollow = false): string {
  const l = Math.hypot(x2 - x1, y2 - y1), ux = (x2 - x1) / l, uy = (y2 - y1) / l, h = 2, w = 0.9;
  const bx = x2 - ux * h, by = y2 - uy * h, f = (n: number) => +n.toFixed(2);
  return P(`M${x1} ${y1}L${f(bx)} ${f(by)}`) + P(`M${x2} ${y2}L${f(bx - uy * w)} ${f(by + ux * w)}L${f(bx + uy * w)} ${f(by - ux * w)}Z`, hollow ? undefined : 'fill');
}
/** Blocked port inside a valve square (T). */
const block = (x: number, y: number, dir: 1 | -1) => P(`M${x} ${y}V${y - 3 * dir}M${x - 1.8} ${y - 3 * dir}H${x + 1.8}`);
/** Exhaust port below a valve square: short stub and open triangle. */
const exhaust = (x: number, y: number) => P(`M${x} ${y}V${y + 2.5}M${x - 1.6} ${y + 2.5}H${x + 1.6}L${x} ${y + 4.5}Z`);
const spring = (x: number, y: number) => P(`M${x} ${y}l1.2 -2.5l1.2 5l1.2 -5l1.2 5l1.2 -2.5`);
/** Solenoid operator: box with the winding diagonal, on the left (dir -1) or right (dir 1) of a valve. */
const solenoid = (x: number, y: number, dir: 1 | -1) => {
  const x0 = dir < 0 ? x - 6 : x;
  return R(x0, y - 3, 6, 6) + P(`M${x0} ${y + 3}L${x0 + 6} ${y - 3}`);
};
const bowtie = 'M0 0H2M14 0H16M2 -4L14 4V-4L2 4Z';

const FL = 'Akışkan (pnömatik / hidrolik)', PID = 'Proses (P&ID)';

export const FLUID_DEFS: SymDef[] = [
  /* ---- actuators ---- */
  {
    key: 'cyl_da', name: 'Çift etkili silindir', cat: FL, prefix: 'MM', role: 'main', fluid: true,
    pins: [[0, 0, 'A'], [28, 0, 'B']], bb: [-4, -16, 44, 0],
    svg: R(-4, -16, 36, 12) + P('M0 0V-4M28 0V-4M6 -16V-4M6 -11H44V-9H6'),
  },
  {
    key: 'cyl_sa', name: 'Tek etkili silindir (yay geri dönüşlü)', cat: FL, prefix: 'MM', role: 'main', fluid: true,
    pins: [[0, 0, 'A']], bb: [-4, -16, 44, 0],
    svg: R(-4, -16, 36, 12) + P('M0 0V-4M6 -16V-4M6 -11H44V-9H6') + P('M8 -10l2 -4l3 8l3 -8l3 8l3 -8l3 8l3 -4'),
  },
  {
    key: 'hmot', name: 'Hidrolik motor', cat: FL, prefix: 'MM', role: 'main', fluid: true,
    pins: [[0, 0, 'A'], [0, -20, 'B']], bb: [-7, -20, 10, 0],
    svg: C(0, -10, 6) + P('M0 0V-4M0 -16V-20') + P('M-2.5 -6.5H2.5L0 -4Z', 'fill') + P('M6 -10H9M8 -11.5L9.5 -10L8 -8.5'),
  },
  /* ---- directional valves ---- */
  {
    key: 'v52_sy', name: 'Valf 5/2, bobinli, yay geri dönüşlü', cat: FL, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [-4, -24, '4'], [4, -24, '2']], bb: [-31, -24, 15, 5],
    svg: R(-8, -20, 16, 16) + R(-24, -20, 16, 16) + P('M0 0V-4M-4 -20V-24M4 -20V-24') + exhaust(-4, -4) + exhaust(4, -4) +
      arrow(0, -4, 4, -20) + arrow(-4, -20, -4, -4) + arrow(-16, -4, -20, -20) + arrow(-12, -20, -12, -4) +
      spring(8, -12) + solenoid(-24, -12, -1),
  },
  {
    key: 'v52_ss', name: 'Valf 5/2, çift bobinli (impuls)', cat: FL, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [-4, -24, '4'], [4, -24, '2']], bb: [-31, -24, 15, 5],
    svg: R(-8, -20, 16, 16) + R(-24, -20, 16, 16) + P('M0 0V-4M-4 -20V-24M4 -20V-24') + exhaust(-4, -4) + exhaust(4, -4) +
      arrow(0, -4, 4, -20) + arrow(-4, -20, -4, -4) + arrow(-16, -4, -20, -20) + arrow(-12, -20, -12, -4) +
      solenoid(8, -12, 1) + solenoid(-24, -12, -1),
  },
  {
    key: 'v32_sy', name: 'Valf 3/2 NC, bobinli, yay geri dönüşlü', cat: FL, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -20, '2']], bb: [-27, -20, 19, 5],
    svg: R(-4, -16, 16, 12) + R(-20, -16, 16, 12) + P('M0 0V-4M0 -16V-20') + exhaust(8, -4) +
      block(0, -4, 1) + arrow(0, -16, 8, -4) + arrow(-16, -4, -16, -16) + block(-8, -4, 1) +
      spring(12, -10) + solenoid(-20, -10, -1),
  },
  {
    key: 'v32_man', name: 'Valf 3/2 NC, butonlu', cat: FL, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -20, '2']], bb: [-27, -20, 19, 5],
    svg: R(-4, -16, 16, 12) + R(-20, -16, 16, 12) + P('M0 0V-4M0 -16V-20') + exhaust(8, -4) +
      block(0, -4, 1) + arrow(0, -16, 8, -4) + arrow(-16, -4, -16, -16) + block(-8, -4, 1) +
      spring(12, -10) + P('M-20 -10H-24M-24 -12.5V-7.5'),
  },
  /* ---- supply, conditioning, flow and pressure ---- */
  {
    key: 'comp', name: 'Kompresör', cat: FL, prefix: 'GP', role: 'main', fluid: true,
    pins: [[0, -20, 'P']], bb: [-9.5, -20, 7, 0],
    svg: C(0, -10, 6) + P('M0 -16V-20') + P('M-2.5 -13.5H2.5L0 -16Z') + P('M-6 -10H-9'),
  },
  {
    key: 'pump_h', name: 'Hidrolik pompa', cat: FL, prefix: 'GP', role: 'main', fluid: true,
    pins: [[0, 0, 'S'], [0, -20, 'P']], bb: [-9.5, -20, 7, 0],
    svg: C(0, -10, 6) + P('M0 0V-4M0 -16V-20') + P('M-2.5 -13.5H2.5L0 -16Z', 'fill') + P('M-6 -10H-9'),
  },
  {
    key: 'filter', name: 'Filtre', cat: FL, prefix: 'HQ', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -20, '2']], bb: [-6, -20, 6, 0],
    svg: P('M0 0V-4M0 -16V-20M0 -4L6 -10L0 -16L-6 -10Z') + P('M-6 -10H6', 'dash'),
  },
  {
    key: 'preg', name: 'Basınç regülatörü', cat: FL, prefix: 'QN', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -20, '2']], bb: [-10, -20, 10, 0],
    svg: R(-6, -16, 12, 12) + P('M0 0V-4M0 -16V-20') + arrow(2, -4, 2, -16) + P('M0 -18H-8V-10H-6', 'dash') +
      P('M6 -10l1 -2l1 4l1 -4l1 2') + P('M-5 -5L5 -15'),
  },
  {
    key: 'chk', name: 'Çek valf', cat: FL, prefix: 'RM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -16, '2']], bb: [-4, -16, 4, 0],
    svg: P('M0 0V-6.5M0 -11V-16M-3.5 -11L0 -7L3.5 -11') + C(0, -9, 2),
  },
  {
    key: 'fcv', name: 'Tek yönlü akış kontrol valfi', cat: FL, prefix: 'RN', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -24, '2']], bb: [-6, -24, 12, 0],
    svg: P('M0 0V-24M-2.5 -16A5 5 0 0 1 -2.5 -8M2.5 -16A5 5 0 0 0 2.5 -8M0 -4H8V-8.5M8 -15.5V-20H0') +
      P('M4.5 -9.5L8 -13.5L11.5 -9.5') + C(8, -11.5, 2) + arrow(-4, -6, 4, -18),
  },
  {
    key: 'gauge', name: 'Manometre', cat: FL, prefix: 'PG', role: 'main', fluid: true,
    pins: [[0, 0, '1']], bb: [-5, -14, 5, 0],
    svg: C(0, -9, 5) + P('M0 0V-4') + arrow(-3, -6, 3, -12),
  },
  {
    key: 'tank', name: 'Hidrolik tank', cat: FL, prefix: 'CM', role: 'main', fluid: true,
    pins: [[0, -12, '1']], bb: [-8, -12, 8, 0],
    svg: P('M-8 -6V0H8V-6M0 -12V-2'),
  },
  {
    key: 'recv', name: 'Hava tankı', cat: FL, prefix: 'CM', role: 'main', fluid: true,
    pins: [[0, 0, '1']], bb: [-6, -24, 6, 0],
    svg: P('M0 0V-4M-6 -8V-18A6 4 0 0 1 6 -18V-8A6 4 0 0 1 -6 -8'),
  },
  {
    key: 'sil', name: 'Susturucu', cat: FL, prefix: 'RN', role: 'main', fluid: true,
    pins: [[0, 0, '1']], bb: [-3, -12, 3, 0],
    svg: P('M0 0V-4M0 -4L3 -8L0 -12L-3 -8Z') + P('M-2 -8H2M-1 -10H1M-1 -6H1'),
  },

  /* ---- process (P&ID) ---- */
  { key: 'pid_gate', name: 'Sürgülü vana', cat: PID, prefix: 'QM', role: 'main', fluid: true, pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -4, 16, 4], svg: P(bowtie) },
  { key: 'pid_globe', name: 'Glob vana', cat: PID, prefix: 'QM', role: 'main', fluid: true, pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -4, 16, 4], svg: P(bowtie) + P('M9.5 0A1.5 1.5 0 0 1 6.5 0A1.5 1.5 0 0 1 9.5 0Z', 'fill') },
  { key: 'pid_ball', name: 'Küresel vana', cat: PID, prefix: 'QM', role: 'main', fluid: true, pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -4, 16, 4], svg: P(bowtie) + C(8, 0, 2.5) },
  { key: 'pid_check', name: 'Çek valf (proses)', cat: PID, prefix: 'RM', role: 'main', fluid: true, pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -4, 16, 4], svg: P(bowtie) + P('M8 0L14 -4V4Z', 'fill') },
  {
    key: 'pid_bfly', name: 'Kelebek vana', cat: PID, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -5, 16, 5], svg: P('M0 0H4M12 0H16M4 -5V5M12 -5V5M5.5 4L10.5 -4') + C(8, 0, 1),
  },
  {
    key: 'pid_ctrl', name: 'Pnömatik kontrol vanası', cat: PID, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -13, 16, 4], svg: P(bowtie) + P('M8 0V-8M3 -8H13A5 5 0 0 0 3 -8'),
  },
  {
    key: 'pid_msv', name: 'Motorlu / solenoid vana', cat: PID, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -14, 16, 4], svg: P(bowtie) + P('M8 0V-8') + R(4, -14, 8, 6) + P('M4 -8L12 -14'),
  },
  {
    key: 'pid_psv', name: 'Emniyet ventili', cat: PID, prefix: 'QM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [8, 8, '2']], bb: [0, -10, 12, 8],
    svg: P('M0 0H2M2 -4L8 0L2 4ZM4 6L8 0L12 6ZM8 6V8') + P('M8 0V-1.5l-2 -1l4 -2l-4 -2l4 -2l-2 -1'),
  },
  {
    key: 'pid_pump', name: 'Santrifüj pompa', cat: PID, prefix: 'GP', role: 'main', fluid: true,
    pins: [[0, 0, 'S'], [16, -4, 'D']], bb: [0, -6, 16, 7],
    svg: C(8, 0, 4) + P('M0 0H4M8 -4H16M5 3.5L3 6.5H13L11 3.5'),
  },
  {
    key: 'pid_tank', name: 'Dikey tank / kap', cat: PID, prefix: 'CM', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [0, -36, '2']], bb: [-8, -36, 8, 0],
    svg: P('M-8 -4V-28A8 4 0 0 1 8 -28V-4A8 4 0 0 1 -8 -4M0 -32V-36'),
  },
  {
    key: 'pid_hx', name: 'Isı eşanjörü', cat: PID, prefix: 'EP', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [24, 0, '2']], bb: [0, -7, 24, 7], svg: C(12, 0, 7) + P('M0 0H5M19 0H24M5 0H7L9 -3L12 3L15 -3L17 0H19'),
  },
  {
    key: 'pid_strainer', name: 'Y süzgeç', cat: PID, prefix: 'HQ', role: 'main', fluid: true,
    pins: [[0, 0, '1'], [16, 0, '2']], bb: [0, -3, 16, 9], svg: P('M0 0H16M5 0L11 8M8.2 9.8L13.8 6.2'),
  },
  {
    // the letters (ISA 5.1, e.g. PT, TIC) and the loop number are drawn from props.isa and the tag
    key: 'pid_inst', name: 'Ölçüm / kontrol cihazı (ISA)', cat: PID, prefix: 'B', role: 'main', fluid: true,
    pins: [[0, 0, '1']], bb: [-6, -16, 6, 0], svg: C(0, -10, 6) + P('M0 0V-4', 'dash'),
  },
];

/** Electrical side: the coil of a solenoid valve. */
export const SOLENOID_DEF: SymDef = {
  key: 'sol', name: 'Valf bobini (solenoid)', cat: 'Kumanda', prefix: 'MB', role: 'main',
  pins: [[0, 0, 'A1'], [0, 20, 'A2']], bb: [-5, 0, 5, 20],
  svg: P('M0 0V6M0 14V20') + R(-5, 6, 10, 8) + P('M-5 14L5 6'),
};

/** Symbol types that carry solenoid references (props.sol, and props.sol2 for the second coil). */
export const SOLENOID_VALVES: Record<string, number> = { v52_sy: 1, v52_ss: 2, v32_sy: 1, pid_msv: 1 };

/** Instrument bubble: circle, ISA letters and the loop number from the tag. */
export function instrumentSvg(isa: string, tag: string): string {
  const t = (y: number, s: number, v: string) => `<text x="0" y="${y}" font-size="${s}" text-anchor="middle">${v.replace(/[<&"]/g, '')}</text>`;
  const no = /\d+[A-Z]?$/.exec(tag)?.[0] ?? '';
  return C(0, -10, 6) + P('M0 0V-4', 'dash') + P('M-6 -10H6') + t(-11.2, 3, (isa || 'PI').slice(0, 4)) + t(-5.8, 2.6, no.slice(0, 5));
}
